import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse, { PaginationHeaders } from "_shared/_api/pagination/responses/paginated-response";
import Guid from "_shared/guids/guid";
import { VendorRolloutBoardJSON } from "legal-entities/vendor-rollouts/_schema/vendor-rollout-board-schema";

/**
 * What `GET legal-entities/vendor-rollouts` is asked with, and what its answer's page says.
 *
 * Kept apart from the RTK Query endpoint so that the views and the tests that draw them can name a
 * column or a page size without loading the store the endpoint is injected into.
 */

/**
 * The columns the board sorts on, spelled as the API's `VendorRolloutBoardOrderField` is.
 */
export enum VendorRolloutBoardOrderField {
  FirmName = "FirmName",
  Status = "Status",
  LastActivity = "LastActivity",
}

/** The largest page the API serves. A larger one is refused with a 400, not clamped. */
export const VENDOR_ROLLOUT_BOARD_MAX_PAGE_SIZE = 100;

/**
 * What one page of the board is asked for with (#5449).
 *
 * Every field goes to the server. The server's defaults are firm name A to Z, every stage, and the
 * first page of 25.
 */
export type VendorRolloutBoardListQuery = {
  /** Part of the firm's name, or of the team leader's name or email address. */
  search?: string;
  /** One of the row `stage` names. */
  status?: string;
  orderBy?: VendorRolloutBoardOrderField;
  orderDir?: OrderDirection;
  pageIndex?: number;
  pageSize?: number;
};

/**
 * Which roll-outs to report on: one client's firms, or one firm's clients, and which page of them.
 *
 * Omitting both ids asks for every client at once, which is the staff question and needs the
 * platform grant. Neither is ever a shorthand for "mine": the API decides scope from what was asked
 * for rather than from who is asking, so a client- or firm-facing surface must always send its own
 * id. Sending both is refused with a 400 — the board reports on one organization at a time, and
 * answering one while dropping the other would return a wider set than was asked for.
 */
export type VendorRolloutBoardRequest = VendorRolloutBoardListQuery & {
  clientLegalEntityId?: Guid;
  vendorLegalEntityId?: Guid;
  includeClosed?: boolean;
};

/**
 * One page of the board, and where it sits in the whole.
 *
 * The body is the board exactly as before paging; the totals arrive in `X-Pagination` beside it.
 * Keeping the body's shape is what lets a bundle that predates paging go on reading the rows it is
 * sent while the API deploys ahead of it.
 */
export type VendorRolloutBoardPage = VendorRolloutBoardJSON & {
  pagination: PaginationHeaders;
};

/** The query string for one page of the board. Anything unset is left off rather than sent. */
export function vendorRolloutBoardParams(request: VendorRolloutBoardRequest): URLSearchParams {
  const parameters = new URLSearchParams();
  if (request.clientLegalEntityId) parameters.set("clientLegalEntityId", request.clientLegalEntityId.value);
  if (request.vendorLegalEntityId) parameters.set("vendorLegalEntityId", request.vendorLegalEntityId.value);
  if (request.includeClosed) parameters.set("includeClosed", "true");
  const search = request.search?.trim();
  if (search) parameters.set("search", search);
  if (request.status) parameters.set("status", request.status);
  if (request.orderBy) parameters.set("orderBy", request.orderBy);
  if (request.orderDir) parameters.set("orderDir", request.orderDir);
  if (request.pageIndex !== undefined) parameters.set("pageIndex", `${request.pageIndex}`);
  if (request.pageSize !== undefined) parameters.set("pageSize", `${request.pageSize}`);
  return parameters;
}

/**
 * The page's place in the whole, from `X-Pagination`.
 *
 * An API that predates paging sends no header and every row in one answer, so a missing header is
 * read as exactly that — one page holding everything — rather than as an empty board of zero.
 */
export function vendorRolloutBoardPagination(
  board: VendorRolloutBoardJSON,
  header: string | null | undefined,
): PaginationHeaders {
  if (header) return PaginatedResponse.parsePaginationHeader(header);
  return {
    pageIndex: 0,
    pageSize: board.rows.length,
    totalElements: board.rows.length,
    totalPages: board.rows.length > 0 ? 1 : 0,
    hasPrev: false,
    hasNext: false,
  };
}
